import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getTransformedUrl } from '@/utils/supabaseImage';
import { MapPin, Star, Calendar, Scissors, Bell, CheckCircle2, Clock, ChevronRight, Phone, Instagram, Loader2, Smartphone } from 'lucide-react';
import { LOGO_URL } from '@/constants';
import { getPublicShopData, PublicShopData } from '@/services/shop/shopPublic';
import { useTranslation } from 'react-i18next';

const DownloadClientApp: React.FC = () => {
    const { t } = useTranslation();
    const { salonId } = useParams<{ salonId: string }>();
    const [salon, setSalon] = useState<PublicShopData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchSalon = async () => {
            try {
                if (!salonId) {
                    setError(true);
                    return;
                }

                const data = await getPublicShopData(salonId);

                if (!data) {
                    setError(true);
                } else {
                    if (data.performances) {
                        const uniquePerformances = [];
                        const seenNames = new Set();
                        for (const perf of data.performances) {
                            if (!seenNames.has(perf.name)) {
                                seenNames.add(perf.name);
                                uniquePerformances.push(perf);
                            }
                        }
                        data.performances = uniquePerformances;
                    }
                    setSalon(data);
                }
            } catch (err) {
                console.error("Unexpected error:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchSalon();
    }, [salonId]);

    // Titolo pagina dinamico
    useEffect(() => {
        if (salon) {
            document.title = `Clip - ${salon.name || 'Prenota'}`;
        } else {
            document.title = 'Clip - Salon Management';
        }

        // Pulizia quando il componente viene smontato
        return () => {
            document.title = 'Clip - Salon Management';
        };
    }, [salon]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
                <Loader2 className="w-10 h-10 text-gray-900 animate-spin mb-4" />
                <p className="text-gray-500 font-medium animate-pulse">{t('download_app.loading', 'Caricamento in corso...')}</p>
            </div>
        );
    }

    if (error || !salon) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                    <Smartphone className="w-10 h-10 text-gray-400" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('download_app.not_found_title', 'Negozio non trovato')}</h1>
                <p className="text-gray-500 max-w-md">{t('download_app.not_found_desc', 'Controlla il link o cerca manualmente sull\'app.')}</p>
            </div>
        );
    }

    // -- Util Functions --
    const checkIsOpenNow = (timeRanges: any) => {
        if (!timeRanges) return false;
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const today = days[new Date().getDay()];
        const todaySchedule = timeRanges[today];

        if (!todaySchedule || !todaySchedule.open || !todaySchedule.close) return false;

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const getMins = (timeStr: string) => {
            if (!timeStr) return 0;
            const [h, m] = timeStr.split(':').map(Number);
            return h * 60 + m;
        };

        const openMins = getMins(todaySchedule.open);
        const closeMins = getMins(todaySchedule.close);

        // Se c'è la pausa
        if (todaySchedule.start_break && todaySchedule.end_break) {
            const startBreakMins = getMins(todaySchedule.start_break);
            const endBreakMins = getMins(todaySchedule.end_break);

            // Aperto se è prima della pausa o dopo la pausa (ed entro la chiusura)
            const isMorningOpen = currentMinutes >= openMins && currentMinutes < startBreakMins;
            const isAfternoonOpen = currentMinutes >= endBreakMins && currentMinutes < closeMins;

            return isMorningOpen || isAfternoonOpen;
        }

        // Orario continuato
        return currentMinutes >= openMins && currentMinutes < closeMins;
    };

    const getFormattedHours = (timeRanges: any) => {
        if (!timeRanges) return [];
        const daysIt = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const dayNames = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

        const dailyStrings = daysIt.map((day, idx) => {
            const schedule = timeRanges[day];

            if (!schedule || !schedule.open || !schedule.close) {
                return { day: dayNames[idx], text: 'Chiuso' };
            }

            if (schedule.start_break && schedule.end_break) {
                return {
                    day: dayNames[idx],
                    text: `${schedule.open} - ${schedule.start_break}, ${schedule.end_break} - ${schedule.close}`
                };
            }

            return {
                day: dayNames[idx],
                text: `${schedule.open} - ${schedule.close}`
            };
        });

        const grouped = [];
        let currentGroup = { startDay: dailyStrings[0].day, endDay: dailyStrings[0].day, text: dailyStrings[0].text };

        for (let i = 1; i < dailyStrings.length; i++) {
            if (dailyStrings[i].text === currentGroup.text) {
                currentGroup.endDay = dailyStrings[i].day;
            } else {
                grouped.push({ ...currentGroup });
                currentGroup = { startDay: dailyStrings[i].day, endDay: dailyStrings[i].day, text: dailyStrings[i].text };
            }
        }
        grouped.push(currentGroup);

        return grouped.map(g => ({
            label: g.startDay === g.endDay ? g.startDay : `${g.startDay} - ${g.endDay}`,
            text: g.text
        }));
    };

    const isOpen = checkIsOpenNow(salon.time_ranges);
    const schedule = getFormattedHours(salon.time_ranges);
    const ratingValue = salon.rating ? salon.rating.toFixed(1) : '5.0';
    const reviewsCount = salon.reviews || 0;

    // Fallbacks visivi se non ha immagini
    // https://images.unsplash.com/photo-1585747860715-2ba37e788b70?q=80&w=2074&auto=format&fit=crop
    const defaultBanner = "https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80";

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-black selection:text-white relative overflow-x-hidden pb-12">
            {/* Page Background Pattern */}
            <div className="absolute inset-0 pointer-events-none opacity-40 z-0"
                style={{ backgroundImage: 'radial-gradient(#e5e7eb 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
            </div>

            {/* Header */}
            <header className="absolute top-6 left-1/2 -translate-x-1/2 md:left-6 md:translate-x-0 z-[99]">
                <div className="bg-[#f2f2f2]/95 backdrop-blur-xl shadow-lg shadow-black/10 rounded-full px-4 py-2 flex items-center justify-center gap-2 border border-white/50">
                    <img src={LOGO_URL} alt="Clip Logo" className="h-5 object-contain" />
                    <span className="text-[11px] font-bold text-gray-500 tracking-wider">OFFICIAL APP</span>
                </div>
            </header>

            {/* Hero / Cover Section */}
            <div className="relative h-64 w-full overflow-hidden">
                <div className="absolute inset-0 bg-black/40 z-10"></div>
                <img
                    src={salon.banner_url || defaultBanner}
                    alt={`${salon.name} Interior`}
                    className="w-full h-full object-cover"
                />
            </div>

            {/* Main Content Container - Overlapping Hero */}
            <div className="relative z-20 -mt-20 px-4 max-w-md mx-auto">

                {/* Salon Header Card */}
                <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-6 border border-gray-100 text-center relative overflow-visible mt-12">

                    {/* Profile Image */}
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2">
                        <div className="relative">
                            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-white shadow-lg bg-white flex items-center justify-center">
                                {salon.logo_url ? (
                                    <div
                                        className="w-full h-full bg-cover bg-center object-contain"
                                        style={{ backgroundImage: `url("${getTransformedUrl(salon.logo_url, 'logo') || salon.logo_url}")` }}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-tr from-orange-400 to-red-500 flex items-center justify-center text-white text-3xl font-bold">
                                        {salon.name.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="absolute -bottom-2 -right-2 bg-blue-500 text-white rounded-full p-1 shadow-md border-2 border-white">
                                <CheckCircle2 className="w-4 h-4" />
                            </div>
                        </div>
                    </div>

                    <div className="mt-12">
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">{salon.name}</h1>
                        <p className="text-gray-500 text-sm mb-4">{salon.description || 'Il tuo stile, la nostra passione.'}</p>

                        <div className="flex justify-center items-center gap-4 text-sm text-gray-600 mb-6">
                            <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg">
                                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                <span className="font-bold text-gray-900">{ratingValue}</span>
                                <span className="text-xs text-gray-400">({reviewsCount})</span>
                            </div>
                            {(salon.address || salon.city) && (
                                <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs truncate max-w-[120px]">{salon.city || salon.address}</span>
                                </div>
                            )}
                            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${isOpen ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                <Clock className="w-4 h-4" />
                                <span className="text-xs font-medium">{isOpen ? 'Aperto ora' : 'Chiuso ora'}</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => document.getElementById('download-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="flex items-center justify-center gap-2 bg-gray-900 text-white py-3 rounded-xl font-medium text-sm hover:bg-black transition-colors shadow-lg shadow-gray-900/20"
                            >
                                <Calendar className="w-4 h-4" />
                                Prenota
                            </button>
                            <a
                                href={salon.phone ? `tel:${salon.phone}` : '#'}
                                className={`flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 py-3 rounded-xl font-medium text-sm transition-colors ${!salon.phone ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                            >
                                <Phone className="w-4 h-4" />
                                Chiama
                            </a>
                        </div>
                    </div>
                </div>

                {/* Services Preview */}
                {salon.performances && salon.performances.length > 0 && (
                    <div className="mt-8">
                        <div className="flex justify-between items-end mb-4 px-2">
                            <h2 className="text-lg font-bold text-gray-900">Servizi Popolari</h2>
                            <button onClick={() => document.getElementById('download-section')?.scrollIntoView({ behavior: 'smooth' })} className="text-xs font-medium text-[#d96c4f] cursor-pointer hover:underline">Vedi tutti</button>
                        </div>

                        <div className="space-y-3">
                            {salon.performances.slice(0, 2).map((perf, idx) => (
                                <div key={perf.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${idx % 2 === 0 ? 'bg-orange-50 text-[#d96c4f]' : 'bg-blue-50 text-blue-500'}`}>
                                            <Scissors className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{perf.name}</h3>
                                            <p className="text-xs text-gray-500">{perf.duration} min • {perf.category.name}</p>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <span className="block font-bold text-gray-900">€ {Math.round(perf.price)}</span>
                                        <button
                                            onClick={() => document.getElementById('download-section')?.scrollIntoView({ behavior: 'smooth' })}
                                            className="text-[10px] font-bold text-white bg-black px-2 py-1 rounded-md mt-1 hover:bg-gray-800 transition-colors"
                                        >
                                            PRENOTA
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Gallery / Portfolio */}
                {salon.gallery && salon.gallery.length > 0 && (
                    <div className="mt-8">
                        <div className="flex justify-between items-center mb-4 px-2">
                            <h2 className="text-lg font-bold text-gray-900">I Nostri Lavori</h2>
                            {salon.instagram && (
                                <div className="flex items-center gap-1 text-xs font-medium text-gray-400">
                                    <a href={salon.instagram}>
                                        <Instagram className="w-5 h-5" />
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                            {salon.gallery.slice(0, 3).map((img, index) => (
                                <div key={img.id} className="aspect-square rounded-xl overflow-hidden bg-gray-100 relative">
                                    <img src={img.image_url} className="w-full h-full object-cover" alt={`Gallery ${index}`} />
                                    {(index === 2 && salon.gallery!.length > 3) && (
                                        <div onClick={() => document.getElementById('download-section')?.scrollIntoView({ behavior: 'smooth' })} className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer hover:bg-black/60 transition-colors">
                                            <span className="text-white text-xs font-bold">+{salon.gallery!.length - 3}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Info & Hours */}
                <div className="mt-8 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Info & Orari</h2>

                    <div className="flex items-start gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
                            <MapPin className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-900">{salon.address}</p>
                            <p className="text-sm text-gray-500">{salon.city}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {schedule.map((item, idx) => (
                            <div key={idx} className={`flex justify-between text-sm ${idx < schedule.length - 1 ? 'border-b border-gray-50 pb-2' : ''}`}>
                                <span className="text-gray-500">{item.label}</span>
                                <span className={`font-medium ${item.text === 'Chiuso' ? 'text-gray-400' : 'text-gray-900'}`}>{item.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* App Promo / Download Section */}
                <div id="download-section" className="mt-32 relative">

                    {/* Phone Image Overlapping */}
                    <div className="absolute -top-32 left-1/2 transform -translate-x-1/2 w-64 z-10 pointer-events-none">
                        <motion.img
                            src="https://xsjxglmijhrkcnqwqhus.supabase.co/storage/v1/object/sign/public-assets/phones-img.webp?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9iZjA5MGQ1MC00ZDFmLTRhMjYtYTE3Zi00Y2JmN2E5ZGExYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJwdWJsaWMtYXNzZXRzL3Bob25lcy1pbWcud2VicCIsImlhdCI6MTc3MTc2MjY0MywiZXhwIjoxODAzMjk4NjQzfQ.0a_1rk4DgiQeF6GreYsTJfEwRROgAb9fg8hn9rBBFOs"
                            alt="App Preview"
                            className="w-full h-auto drop-shadow-2xl"
                            animate={{ y: [0, -15, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </div>

                    <div className="bg-[#0F1115] rounded-3xl p-6 pt-32 text-white relative overflow-hidden text-center shadow-2xl">
                        {/* Dot Pattern inside Download Section */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none"
                            style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '16px 16px' }}>
                        </div>

                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#d96c4f] rounded-full blur-[60px] opacity-20 pointer-events-none"></div>

                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-2">Scarica l'App</h2>
                            <p className="text-gray-400 text-sm mb-8 max-w-xs mx-auto">
                                Per prenotare da {salon.name} e gestire i tuoi appuntamenti, scarica Clip.
                            </p>

                            <div className="flex flex-col gap-3">
                                <a href="https://apps.apple.com/it/app/clip/id6757597296" target="_blank" rel="noopener noreferrer" className="w-full bg-white text-black rounded-xl py-3 px-4 flex items-center justify-center gap-3 hover:bg-gray-100 transition-all active:scale-95">
                                    <svg viewBox="0 0 384 512" width="18" fill="currentColor">
                                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z" />
                                    </svg>
                                    <div className="flex flex-col items-start">
                                        <span className="text-[9px] uppercase tracking-wider opacity-60 leading-none mb-0.5">Scarica su</span>
                                        <span className="text-sm font-bold leading-none">App Store</span>
                                    </div>
                                </a>

                                <a href="https://play.google.com/store/apps/details?id=com.cobblestone.clip&hl=it" target="_blank" rel="noopener noreferrer" className="w-full bg-white/10 backdrop-blur-md text-white border border-white/10 rounded-xl py-3 px-4 flex items-center justify-center gap-3 hover:bg-white/20 transition-all active:scale-95">
                                    <svg viewBox="0 0 512 512" width="18" fill="currentColor">
                                        <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                                    </svg>
                                    <div className="flex flex-col items-start">
                                        <span className="text-[9px] uppercase tracking-wider opacity-60 leading-none mb-0.5">DISPONIBILE SU</span>
                                        <span className="text-sm font-bold leading-none">Google Play</span>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DownloadClientApp;
